import classNames from "classnames";
import { isNil } from "lodash";
import { Key } from "react-aria";
import { Tab, TabList, TabPanel, Tabs } from "react-aria-components";

export function TabBar({
  children,
  className,
  defaultValue,
  value,
  onChange = () => {},
}: React.PropsWithChildren<{
  className?: string;
  defaultValue?: string;
  value?: Key;
  onChange?: (key: Key) => void;
}>) {
  return (
    <Tabs
      orientation='horizontal'
      selectedKey={value}
      onSelectionChange={onChange}
      className={className}
      defaultSelectedKey={defaultValue}
    >
      {children}
    </Tabs>
  );
}

function TabBarTabList({
  children,
  label,
  className,
}: React.PropsWithChildren<{ className?: string; label?: string }>) {
  return (
    <TabList
      className={classNames(
        "tw-flex tw-gap-[28px] tw-border-0 tw-border-b-[1px] tw-border-solid tw-border-border tw-px-[21px]",
        className,
      )}
      aria-label={label}
    >
      {children}
    </TabList>
  );
}

export function TabBarTab({
  children,
  itemCount,
  needsAttention,
  id,
  className,
}: React.PropsWithChildren<{
  active?: boolean;
  itemCount?: number;
  needsAttention?: boolean;
  id: string;
  className?: string;
}>) {
  return (
    <Tab
      className={classNames(
        "tw-group tw-m-0 tw-border-none tw-bg-transparent tw-p-0 focus:tw-outline-none focus-visible:tw-outline-none",
        className,
      )}
      id={id}
    >
      {({ isSelected }) => {
        return (
          <>
            <div className='tw-flex tw-h-[36px] tw-cursor-pointer tw-flex-col tw-py-[10px] tw-pb-[7px]'>
              <div className='tw-flex tw-items-center tw-justify-center tw-gap-3 group-focus-visible:tw-rounded-sm group-focus-visible:tw-ring-2 group-focus-visible:tw-ring-primary'>
                <span
                  className={classNames({
                    "tw-font-bold": isSelected,
                  })}
                >
                  {children}
                </span>
                {!isNil(itemCount) && <span className='tw-text-text-secondary'>{itemCount}</span>}
                {needsAttention && (
                  <div className='tw-h-[7px] tw-w-[7px] tw-flex-shrink-0 tw-flex-grow-0 tw-rounded-full tw-bg-red'></div>
                )}
              </div>
              <div className='tw-not-sr-only tw-invisible -tw-my-[16px] tw-flex tw-items-center tw-gap-3'>
                <span className='tw-font-bold'>{children}</span>
                {!isNil(itemCount) && <span className='tw-text-text-secondary'>{itemCount}</span>}
                {needsAttention && (
                  <div className='tw-h-[7px] tw-w-[7px] tw-flex-shrink-0 tw-flex-grow-0 tw-rounded-full tw-bg-red'></div>
                )}
              </div>
            </div>
            <div
              className={classNames(
                `-tw-mb-[1px] tw-h-[3px] tw-w-full tw-transition-colors tw-duration-150 group-hover:tw-visible group-hover:tw-opacity-100 group-hover:[&:not(.tw-bg-primary)]:tw-bg-border-dark group-hover:[&:not(.tw-bg-primary)]:tw-duration-75`,
                {
                  "tw-opacity-0": !isSelected,
                  "tw-bg-primary": isSelected,
                },
              )}
            ></div>
          </>
        );
      }}
    </Tab>
  );
}

export function TabBarTabPanel({
  children,
  id,
  className,
}: React.PropsWithChildren<{
  id: string;
  className?: string;
}>) {
  return (
    <TabPanel className={className} id={id}>
      {children}
    </TabPanel>
  );
}

TabBar.TabList = TabBarTabList;
TabBar.Tab = TabBarTab;
TabBar.TabPanel = TabBarTabPanel;
